<script context="module" lang="ts">
	import type { SectionConfig } from '$lib/util/pagebuilder';
	import DirectusLink from '$lib/components/navigation/DirectusLink.svelte';
	import { gql } from 'graphql-request';

	import Section from '$lib/components/layout/Section.svelte';
	import Text from '$lib/components/Text.svelte';
	import Media from '$lib/components/media/Media.svelte';
	import { getDirectus } from '$lib/util/directus';

	import { PUBLIC_DIRECTUS_BACKEND } from '$env/static/public';

	import { page } from '$app/stores';
	import { onMount } from 'svelte';

	export async function load(data, params) {
		let query = `items/locations
				?fields[]=*
				&fields[]=translations.*.*
				&fields[]=map_icon.*.*`;

		const locations = await getDirectus(query, null, params.lang);

		return {
			locations: locations.data
		};
	}

	export const config: SectionConfig = {
		collection: 'section_map',
		availableFor: ['pages', 'special-pages'],
		load,
		query: gql`
			lat
			lng
			zoom
			show_location_list
			retail_partners {
				media {
					...Media
				}
				link
			}
      translations {
        languages_code {
          code
        }
        title
        subtitle
				button_title
      }
			button_link {
				...Link
			}
    `
	};

	// optional: define a type for the props (note: injectTranslations is applied)
	type Props = {
		title: string;
		subtitle: string;
		people: Array<any>;
		lat: string;
		lng: string;
		zoom: string;
		locations: Array<any>;
		map_id: string;
		show_location_list: boolean;
		retail_partners: any[];
	};
</script>

<script lang="ts">
	import viewport from '$lib/actions/useViewportAction';
	import * as gmapsLoader from '@googlemaps/js-api-loader';
	const { Loader } = gmapsLoader;

	import GlobalMedia from '$lib/components/media/GlobalMedia.svelte';
	import { fade } from 'svelte/transition';
	import Link from '$lib/components/navigation/Link.svelte';
	import Button from '$lib/components/util/Button.svelte';

	export let data: Props; // the components gets all data via this prop (additionally each top level prop is directly provided to the component)
	export let additionalData: Record<string, any>; // additional data loaded using the load function
	export let name: string;

	let map: any;
	let markerIcon: any = {};
	$: if ('map_marker' in $page.data.globalMedias) {
		markerIcon = $page.data.globalMedias['map_marker'];
	}

	let activeMarkerIcon: any = {};
	$: if ('map_marker_active' in $page.data.globalMedias) {
		activeMarkerIcon = $page.data.globalMedias['map_marker_active'];
	}

	let hoverMarkerIcon: any = {};
	$: if ('map_marker_hover' in $page.data.globalMedias) {
		hoverMarkerIcon = $page.data.globalMedias['map_marker_hover'];
	}

	let activeLocation: any = null;
	let activeLocationIdx: number = -1;
	let hoveredLocationIdx: number = -1;
	let sortedlocations: any[] = [];
	let markers: any[] = [];
	let search: string;
	let searchCoords: any;
	let changeTimer: any;
	let mapElement: any;

	let autocomplete: any;

	async function init() {
		const loader = new Loader({
			apiKey: $page.data.pageConfig?.maps_api_key,
			version: 'weekly',
			libraries: ['places']
		});

		await loader.load();

		const { Map } = (await google.maps.importLibrary('maps')) as google.maps.MapsLibrary;
		map = new Map(document.getElementById('map-' + name) as HTMLElement, {
			center: { lat: parseFloat(data.lat), lng: parseFloat(data.lng) },
			zoom: parseFloat(data.zoom),
			styles: $page.data.pageConfig?.maps_style,
			mapTypeControl: false
		});

		google.maps.event.addListener(map, 'click', function () {
			deselect();
		});

		// Create markers
		additionalData.locations.forEach((location, idx) => {
			const marker = new google.maps.Marker({
				map,
				position: {
					lat: parseFloat(location.lat),
					lng: parseFloat(location.lng)
				},
				icon: {
					url: PUBLIC_DIRECTUS_BACKEND + 'assets/' + markerIcon?.id,
					scaledSize: new google.maps.Size(16, 20)
				}
			});
			markers.push(marker);

			const i = idx;

			google.maps.event.addListener(marker, 'click', function () {
				if (activeLocationIdx !== -1) {
					markers[activeLocationIdx].setIcon({
						url: PUBLIC_DIRECTUS_BACKEND + 'assets/' + markerIcon.id,
						scaledSize: new google.maps.Size(16, 20)
					});
				}

				activeLocationIdx = i;
				activeLocation = additionalData.locations[i];

				marker.setIcon({
					url: PUBLIC_DIRECTUS_BACKEND + 'assets/' + activeMarkerIcon?.id,
					scaledSize: new google.maps.Size(32, 40)
				});

				const markerPosition = {
					lat: parseFloat(location.lat),
					lng: parseFloat(location.lng)
				};
				// map.panTo(markerPosition);
				// map.setZoom(15);

				searchCoords = markerPosition;
				getNearestShops();
				selectMarker(location.id);
				return false;
			});

			google.maps.event.addListener(marker, 'mouseover', function () {
				if (activeLocationIdx !== i) {
					marker.setIcon({
						url: PUBLIC_DIRECTUS_BACKEND + 'assets/' + hoverMarkerIcon.id,
						scaledSize: new google.maps.Size(16, 20)
					});
				}
			});

			google.maps.event.addListener(marker, 'mouseout', function () {
				if (activeLocationIdx !== i && hoveredLocationIdx !== i) {
					marker.setIcon({
						url: PUBLIC_DIRECTUS_BACKEND + 'assets/' + markerIcon.id,
						scaledSize: new google.maps.Size(16, 20)
					});
				}
			});
		});

		// Autocomplete
		const center = { lat: parseFloat(data.lat), lng: parseFloat(data.lng) };
		const defaultBounds = {
			north: center.lat + 0.1,
			south: center.lat - 0.1,
			east: center.lng + 0.1,
			west: center.lng - 0.1
		};

		const options = {
			bounds: defaultBounds,
			fields: ['address_components', 'geometry'],
			strictBounds: false
		};
		autocomplete = new google.maps.places.Autocomplete(
			document.getElementById('map-search-input'),
			options
		);

		autocomplete.addListener('place_changed', getAddressData);
	}

	function getNearestShops() {
		const locations = [];
		for (const location of additionalData.locations) {
			const distance = calcDistance(searchCoords.lat, searchCoords.lng, location.lat, location.lng);
			if (distance < 100) {
				locations.push({
					distance,
					location: location
				});
			}
		}

		locations.sort(function (a, b) {
			if (a.distance < b.distance) {
				return -1;
			}
			if (a.distance > b.distance) {
				return 1;
			}
			return 0;
		});

		sortedlocations = locations;
		deselect();
	}

	function calcDistance(lat1, lon1, lat2, lon2) {
		/*eslint-disable*/
		const R = 6371; // km
		const dLat = ((lat2 - lat1) * Math.PI) / 180;
		const dLon = ((lon2 - lon1) * Math.PI) / 180;
		lat1 = (lat1 * Math.PI) / 180;
		lat2 = (lat2 * Math.PI) / 180;

		const a =
			Math.sin(dLat / 2) * Math.sin(dLat / 2) +
			Math.sin(dLon / 2) * Math.sin(dLon / 2) * Math.cos(lat1) * Math.cos(lat2);
		const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
		const d = R * c;
		return d;
		/*eslint-dsable*/
	}

	function getAddressData() {
		let addressData = autocomplete.getPlace();

		if (addressData.geometry) {
			searchCoords = {
				lat: addressData.geometry.location.lat(),
				lng: addressData.geometry.location.lng()
			};

			map.panTo(searchCoords);
			map.setZoom(15);

			getNearestShops();
		}
	}

	let loaded = false;

	async function handleEnterViewport() {
		if (!loaded) {
			await init();
			loaded = true;
		}
	}

	// function onChange(...args: any[]) {
	// 	clearTimeout(changeTimer);
	// 	changeTimer = setTimeout(() => {
	// 		autoComplete;
	// 	}, 500);
	// }

	function autoComplete(text: string) {
		const place = autocomplete.getPlace();
	}

	function selectMarker(locationId: number) {
		additionalData.locations.forEach((location, idx) => {
			if (location.id === locationId) {
				deselect();
				activeLocation = location;
				activeLocationIdx = idx;
				markers[activeLocationIdx].setIcon({
					url: PUBLIC_DIRECTUS_BACKEND + 'assets/' + activeMarkerIcon?.id,
					scaledSize: new google.maps.Size(32, 40)
				});
				map.panTo({
					lat: parseFloat(location.lat),
					lng: parseFloat(location.lng)
				});
				map.setZoom(15 + Math.random() * 0.01);
			}
		});
		if (mapElement && window.innerWidth < 1024) {
			mapElement.scrollIntoView({ behavior: 'smooth' });
		}
	}

	function deselect() {
		if (activeLocationIdx >= 0) {
			markers[activeLocationIdx].setIcon({
				url: PUBLIC_DIRECTUS_BACKEND + 'assets/' + markerIcon.id,
				scaledSize: new google.maps.Size(16, 20)
			});
			activeLocation = null;
			activeLocationIdx = -1;
		}
	}

	function handleMouseEnter(locationId: number) {
		const markerIndex = markers.findIndex((marker) => {
			const location = additionalData.locations.find((loc) => loc.id === locationId);
			return (
				marker.position.lat() === parseFloat(location?.lat) &&
				marker.position.lng() === parseFloat(location?.lng)
			);
		});
		if (markerIndex !== -1 && activeLocationIdx !== markerIndex) {
			markers[markerIndex].setIcon({
				url: PUBLIC_DIRECTUS_BACKEND + 'assets/' + hoverMarkerIcon.id,
				scaledSize: new google.maps.Size(16, 20)
			});
		}
	}
	function handleMouseLeave(locationId: number) {
		const markerIndex = markers.findIndex((marker) => {
			const location = additionalData.locations.find((loc) => loc.id === locationId);
			return (
				marker.position.lat() === parseFloat(location?.lat) &&
				marker.position.lng() === parseFloat(location?.lng)
			);
		});
		if (markerIndex !== -1 && activeLocationIdx !== markerIndex) {
			markers[markerIndex].setIcon({
				url: PUBLIC_DIRECTUS_BACKEND + 'assets/' + markerIcon.id,
				scaledSize: new google.maps.Size(16, 20)
			});
		}
	}

	function reset() {
		search = '';
		sortedlocations = [];
	}
</script>

<div class="intersection-trigger" use:viewport on:enterViewport={handleEnterViewport} />
<Section>
	<div class="header">
		<h2>{data.title}</h2>
		<div class="header-divider" />
		<div class="subtitle">{data.subtitle}</div>
		<div class="partners" bind:this={mapElement}>
			{#each Array(4) as i}
				<div
					class="partner-wrapper {$page.data.langDirection}"
					style="animation-duration: {4 * (data.retail_partners?.length ?? 1)}s"
				>
					{#each data.retail_partners as partner}
						<div class="partner">
							<Media {...partner.media} />
						</div>
					{/each}
				</div>
			{/each}
		</div>
	</div>
	<div class="map-divider" />
	<div class="map-container">
		<div id="map-{name}" class="map" />
		<div class="overlay">
			<div class="info-wrapper"><Text key="map_info" /></div>
			<div class="input-wrapper">
				<button type="button" class="search-icon">
					<GlobalMedia key="icon-search2" />
				</button>
				<input type="text" id="map-search-input" bind:value={search} />

				<button type="button" class="close-icon" on:click={() => reset()}>
					<GlobalMedia key="icon-close" />
				</button>
			</div>
			{#if activeLocation && sortedlocations?.length === 0}
				<div class="single-location">
					<div class="your-store"><Text key="your_store" /></div>
					<div class="name">{activeLocation?.name}</div>
					<div class="address-wrapper">
						{#if activeLocation?.street}
							<div class="address">
								{activeLocation?.street ?? ''}, {activeLocation?.city ?? ''}
							</div>
						{/if}
						<a
							href="https://google.com/maps?q={activeLocation.lat},{activeLocation.lng}"
							target="_blank"
							class="navigate-to"
						>
							<GlobalMedia key="icon_directions" />
						</a>
					</div>
				</div>
			{/if}
			{#if sortedlocations?.length > 0}
				<div class="locations">
					<div class="title">
						<Text key="your_store" />
					</div>
					<div class="divider" />
					{#each sortedlocations.slice(0, 1) as { location }, idx}
						<div
							class="location"
							class:active={location === activeLocation}
							on:click={() => selectMarker(location.id)}
						>
							<div class="name">{location.name}</div>
							<div class="address-row">
								{#if location?.street}
									<div class="address">{location.street ?? ''}, {location.city ?? ''}</div>
								{/if}
								<a
									href="https://google.com/maps?q={location.lat},{location.lng}"
									target="_blank"
									class="navigate-to"
								>
									<GlobalMedia key="icon_directions" />
								</a>
							</div>
						</div>
					{/each}
				</div>
			{/if}
			{#if sortedlocations?.length > 1}
				<div class="locations more">
					<div class="title">
						<Text key="map_more_options" />
					</div>
					<div class="divider" />
					<div class="locations-wrapper">
						{#each sortedlocations.slice(1) as { location }, idx}
							<div
								class="location"
								class:active={location === activeLocation}
								on:click={() => selectMarker(location.id)}
								on:mouseenter={() => handleMouseEnter(location.id)}
								on:mouseleave={() => handleMouseLeave(location.id)}
							>
								<div class="name small">{location.name}</div>
								{#if location?.street}
									<div class="address">{location.street ?? ''}, {location.city ?? ''}</div>
								{/if}
							</div>
							{#if idx !== sortedlocations.length - 2}
								<div class="divider" />
							{/if}
						{/each}
					</div>
				</div>
			{/if}
		</div>
	</div>
	<div class="link">
		{data.button_title}
	</div>
	<DirectusLink {...data.button_link}>
		<Button style="yellow" {...data.button_link} />
	</DirectusLink>
</Section>

<style lang="scss">
	@keyframes map-partners {
		0% {
			transform: translateX(0);
		}
		100% {
			transform: translateX(-100%);
		}
	}

	@keyframes map-partners-rtl {
		0% {
			transform: translateX(0);
		}
		100% {
			transform: translateX(100%);
		}
	}

	.header {
		margin-bottom: v(content-padding-lg);

		h2 {
			@include font(h2);
			margin-bottom: v(content-padding);
		}

		.subtitle {
			@include font(h5);
			margin-bottom: v(content-padding);
		}

		.header-divider {
			width: 80%;
			height: 1px;
			background: color(black);
			margin-bottom: 80px;
		}

		.partners {
			overflow: hidden;
			display: flex;

			.partner-wrapper {
				display: flex;
				gap: v(content-padding-xl);
				padding-inline-end: v(content-padding-xl);
				flex-shrink: 0;
				animation: map-partners linear infinite;

				&.rtl {
					animation: map-partners-rtl linear infinite;
				}
			}
			.partner {
				width: 100%;
				max-width: 200px;
			}

			.partner :global(img) {
				height: 64px;
				flex-shrink: 0;
			}
		}
	}

	.map-divider {
		width: 100%;
		height: 1px;
		background: color(grey_05);
		margin: v(content-padding-lg) 0;
	}

	.map-container {
		position: relative;

		.overlay {
			display: flex;
			flex-direction: column;
			position: absolute;
			inset: v(content-padding);
			z-index: 2;
			max-width: 350px;
			overflow: hidden;

			@media (max-width: 1350px) {
				max-width: 400px;
			}

			@media (max-width: 1100px) {
				max-width: 360px;
			}

			@include media(tablet) {
				position: relative;
				inset: 0;
				max-width: none;
			}
			.your-store {
				border-bottom: solid 1px color(black);
				@include font(h6);
				padding-bottom: v(content-gap-xs);
			}
			.info-wrapper {
				background: color(yellow);
				text-transform: uppercase;
				border-radius: v(border-radius);
				@include font(h5);
				padding: v(content-padding-xs) v(content-padding);
				margin-bottom: v(content-gap-xs);
			}

			.input-wrapper {
				width: 100%;
				display: flex;
				align-items: center;
				background: white;
				padding: 0 16px;
				margin-bottom: v(content-padding);
				border-radius: 4px;

				input {
					width: 100%;
					border: none;
					@include font(h6);
					background: transparent;
					padding: 16px;
				}

				.search-icon {
					width: 40px;
					height: 40px;
					background: transparent;
					border: none;
				}

				.close-icon {
					width: 40px;
					height: 40px;
					background: transparent;
					border: none;
				}
			}
		}

		.map {
			width: 100%;
			height: 50vh;
			margin-bottom: v(content-padding);
		}
	}

	.wrapper {
		display: flex;
		flex-direction: column;
		align-items: center;
	}

	.single-location {
		background: white;
		padding: 16px;
		margin-bottom: v(content-padding-xs);
		border-radius: 4px;
		@include font(text);

		.name {
			@include font(h4);
			margin-bottom: 8px;
			margin-top: v(content-gap-xs);
		}
	}

	.locations {
		background: white;
		padding: 16px;
		margin-bottom: v(content-padding-xs);
		border-radius: 4px;
		&.more {
			.location {
				&:hover {
					background: color(grey);
					border-radius: v(border-radius);
				}
			}
		}

		.location {
			transition: all 0.3s ease;
			padding-block: v(content-padding-sm);
			// margin: v(content-padding-xs) 0;
			border-radius: 4px;
			cursor: pointer;

			// &:hover,
			// &.active {
			// 	// background: color(yellow);
			// 	padding-inline-start: v(content-gap);
			// }
		}

		.address-row {
			display: flex;
			justify-content: space-between;

			.navigate-to {
				height: 1.5em;
				@include transition(all);

				&:hover {
					opacity: 0.5;
				}
			}
		}

		&.more {
			overflow-y: auto;
		}

		.title {
			@include font(h6);
			margin-bottom: v(content-padding-xs);
		}

		.divider {
			width: 100%;
			height: 1px;
			background: color(black);
		}

		.name {
			@include font(h4);
			margin-bottom: 8px;

			&.small {
				@include font(h6);
			}
		}
	}

	.link {
		@include font(h5);
		margin-bottom: v(content-padding-sm);
		margin-top: 80px;

		@media (max-width: 768px) {
			margin-top: 40px;
		}
	}
	.address-wrapper {
		display: flex;
		justify-content: space-between;
	}
</style>
